import { CalendarDays, ShieldCheck, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useData } from './DataContext';
import { getSupabase } from './supabase';

export function PlayerMatchdayView() {
  const { team, matches, lineups, players } = useData();
  const [quickPlayerId, setQuickPlayerId] = useState('');
  const [publicStats, setPublicStats] = useState<Record<string,{apps:number;minutes:number;goals:number;assists:number;avg_rating:number|null}>>({});
  const now = new Date();

  const nextMatch = useMemo(() => {
    const future = matches
      .filter((match) => new Date(`${match.match_date}T12:00:00`) >= new Date(now.toDateString()))
      .sort((a,b) => a.match_date.localeCompare(b.match_date));
    return future[0] || null;
  }, [matches]);

  const published = lineups[0] || null;
  const playerById = useMemo(() => new Map(players.map((p) => [p.id,p])), [players]);
  const slots = published?.slots && typeof published.slots === 'object'
    ? Object.entries(published.slots as Record<string,string|null>)
        .filter(([,playerId]) => Boolean(playerId))
        .map(([slot,playerId]) => ({ slot, player: playerById.get(String(playerId)) }))
        .filter((item) => Boolean(item.player))
    : [];

  useEffect(() => {
    const client = getSupabase();
    if (!client || !team?.id) return;
    let cancelled = false;
    void client.from('player_public_stats').select('*').eq('team_id', team.id).then(({ data, error }) => {
      if (cancelled || error) return;
      const next: Record<string,{apps:number;minutes:number;goals:number;assists:number;avg_rating:number|null}> = {};
      for (const row of data ?? []) {
        next[String(row.player_id)] = {
          apps: Number(row.apps ?? 0), minutes: Number(row.minutes ?? 0), goals: Number(row.goals ?? 0),
          assists: Number(row.assists ?? 0), avg_rating: row.avg_rating == null ? null : Number(row.avg_rating),
        };
      }
      setPublicStats(next);
    });
    return () => { cancelled = true; };
  }, [team?.id]);

  const quickPlayer = quickPlayerId ? playerById.get(quickPlayerId) || null : null;
  const quickStats = quickPlayer ? publicStats[quickPlayer.id] : undefined;

  return (
    <div className="player-readonly-page">
      <section className="metal-card player-readonly-hero">
        <div><p className="eyebrow">PLAYER MATCHDAY</p><h1>My Matchday</h1><p className="muted">Published information from the coaching team.</p></div>
        <CalendarDays size={34}/>
      </section>

      <section className="metal-card player-next-match">
        <p className="eyebrow">NEXT MATCH</p>
        {nextMatch ? (
          <>
            <h2>Polonia Glasgow AFC <span>vs</span> {nextMatch.opponent || 'TBA'}</h2>
            <p>{new Date(`${nextMatch.match_date}T12:00:00`).toLocaleDateString()} · {nextMatch.competition || 'Competition TBA'}</p>
          </>
        ) : <p className="muted">No upcoming match published yet.</p>}
      </section>

      <section className="metal-card player-published-lineup">
        <div className="player-section-head">
          <div><p className="eyebrow">COACH PUBLISHED</p><h2>Starting XI / Squad</h2></div>
          <ShieldCheck size={25}/>
        </div>
        {!published ? (
          <div className="player-lineup-pending"><Users size={22}/><strong>Lineup not published yet</strong><span>You will see it here after the coaches publish it.</span></div>
        ) : (
          <>
            <div className="player-lineup-meta">
              <strong>{published.name || 'Matchday Lineup'}</strong>
              <span>{published.formation || 'Formation TBA'}</span>
            </div>
            <div className="player-lineup-list">
              {slots.map(({slot,player}) => (
                <button type="button" className="player-lineup-row" key={slot} onClick={() => player && setQuickPlayerId(player.id)}>
                  <span>{slot}</span>
                  <b>#{player?.squad_number ?? '—'}</b>
                  <strong>{player?.name}</strong>
                  <small>{player?.position || ''}</small>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {quickPlayer && (
        <div className="lineup-quickview-backdrop player-public-quickview-backdrop" role="dialog" aria-modal="true" aria-label={`${quickPlayer.name} matchday details`} onClick={() => setQuickPlayerId('')}>
          <article className="lineup-quickview-card player-public-quickview" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="lineup-quickview-close" onClick={() => setQuickPlayerId('')} aria-label="Close"><X size={19}/></button>
            <div className="lineup-quickview-photo"><img src={quickPlayer.photo_url || '/polonia-forged-crest.png'} alt={quickPlayer.name}/></div>
            <div className="lineup-quickview-head"><span>#{quickPlayer.squad_number ?? '—'} · {quickPlayer.position || '—'}</span><h2>{quickPlayer.name}</h2></div>
            <div className="lineup-quickview-stats">
              <span><small>Form</small><b>{Number(quickPlayer.current_form ?? 0).toFixed(1)}</b></span>
              <span><small>Apps</small><b>{quickStats?.apps ?? 0}</b></span>
              <span><small>Minutes</small><b>{quickStats?.minutes ?? 0}</b></span>
              <span><small>Goals</small><b>{quickStats?.goals ?? 0}</b></span>
              <span><small>Assists</small><b>{quickStats?.assists ?? 0}</b></span>
              <span><small>Avg rating</small><b>{quickStats?.avg_rating != null ? quickStats.avg_rating.toFixed(1) : '—'}</b></span>
            </div>
          </article>
        </div>
      )}
    </div>
  );
}
